import {
  getGridDateOperators,
  getGridNumericOperators,
  getGridStringOperators,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridFilterOperator,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticalVariableListQuery } from '@verantos-dev/feature-analytical-variables';

import { useAdRouteParams } from './useAdRouteParams';
import type { AnalyticalDatasetResultRow } from '../api/types';
import { useAnalyticalDatasetProfileQuery } from '../api/useAnalyticalDatasetProfileQuery';
import { useAnalyticalDatasetResultsQuery } from '../api/useAnalyticalDatasetResultsQuery';
import { useAnalyticalDatasetVersionQuery } from '../api/useAnalyticalDatasetVersionQuery';
import { buildDateBetweenFilterOperator } from '../components/DateBetweenFilterInput';
import { selectHasStagedChanges } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';
import { formatIsoDate } from '../utils/formatIsoDate';

const EMPTY_CELL = '—';

// System demographic columns hidden by default once the dataset has any
// analytical variable — they'd otherwise crowd out the AV columns the user
// came to see. patient_id + index_date always stay visible. When there are no
// AVs, all of these show by default.
const DEMOGRAPHIC_FIELDS = ['age_at_index', 'gender', 'race', 'ethnicity'] as const;

// Default sort mirrors the server's fallback ordering (patient_id ascending);
// every column is server-sortable, and patient_id tiebreaks non-unique sorts
// on the backend so pagination stays deterministic.
const PATIENT_ID_FIELD = 'patient_id';
export const RESULTS_DEFAULT_SORT_MODEL = [{ field: PATIENT_ID_FIELD, sort: 'asc' as const }];

// Only the operators the results endpoint translates to SQL (see query_utils'
// grid_query_sql): isEmpty/isNotEmpty serialize without a value and isAnyOf's
// array value breaks the `field:operator(value)` wire format — offering them
// would show a "filtered" grid the server never filtered.
const SUPPORTED_STRING_OPERATORS = new Set([
  'contains',
  'doesNotContain',
  'equals',
  'doesNotEqual',
  'startsWith',
  'endsWith',
]);
const SUPPORTED_NUMERIC_OPERATORS = new Set(['=', '!=', '>', '>=', '<', '<=']);
// Date columns project as YYYY-MM-DD strings and MUI's date input emits the
// same shape, so the backend compares them lexicographically (= chronologically).
const SUPPORTED_DATE_OPERATORS = new Set([
  'is',
  'not',
  'after',
  'onOrAfter',
  'before',
  'onOrBefore',
]);

// Boolean AV values arrive as the raw strings "true"/"false"; show them
// title-cased. Anything unexpected passes through unchanged.
const formatBooleanCell = (value: string): string => {
  const normalized = value.toLowerCase();
  if (normalized === 'true') return 'True';
  if (normalized === 'false') return 'False';
  return value;
};

// Placeholder columns rendered before the first response arrives: the shared
// DataGrid's skeleton loading overlay draws per-column skeleton cells, so with
// zero columns the first load shows a blank card instead of a skeleton. The
// system columns are the stable prefix of the server column list (fields,
// names and types mirror the backend's _SYSTEM_COVARIATES), so the header row
// doesn't jump when the real columns swap in — AV placeholder columns (built
// in the hook from the version's variables + the AV library) append after.
const SYSTEM_SKELETON_COLUMNS: GridColDef<AnalyticalDatasetResultRow>[] = [
  { field: 'patient_id', headerName: 'Patient ID' },
  { field: 'index_date', headerName: 'Index date', type: 'date' },
  { field: 'age_at_index', headerName: 'Age', type: 'number' },
  { field: 'gender', headerName: 'Gender' },
  { field: 'race', headerName: 'Race' },
  { field: 'ethnicity', headerName: 'Ethnicity' },
];

// Mirrors the backend's _COLUMN_TYPE_BY_FORMAT (keyed on the lower-cased AV
// format) so a skeleton AV column carries the same type — and thus the same
// sort/filter affordances — as the real column that replaces it. Formats the
// backend leaves as plain strings (e.g. NUMERIC) stay untyped here too.
const SKELETON_TYPE_BY_FORMAT: Record<string, GridColDef['type']> = {
  date: 'date',
  datetime: 'dateTime',
  number: 'number',
  boolean: 'boolean',
};

// Fallback column width for the first-load skeleton columns (which carry no
// width). Real columns use the server-provided minWidth (ColumnDefinition owns
// it) so a handful fit the viewport and the rest scroll horizontally.
const SKELETON_COLUMN_MIN_WIDTH = 200;

export interface ResultsTabEmptyState {
  id: string;
  title: string;
  message: string;
}

export const useAnalyticalDatasetResultsTab = () => {
  const { formatMessage } = useIntl();
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  const { data: profileEnvelope, isLoading: isProfileLoading } = useAnalyticalDatasetProfileQuery({
    projectId,
    analyticalDatasetProfileId,
  });
  const latestStatus = profileEnvelope?.data.latestStatus;
  const isProcessed = latestStatus === 'PROCESSED';

  // Warn that the displayed results predate unsaved definition edits (mirrors
  // the Definition tab's banner). Only meaningful once there ARE results, i.e.
  // PROCESSED — which is also the only state that renders the grid.
  const hasStagedChanges = useAnalyticalDatasetDefinitionStore(selectHasStagedChanges);
  const showStagedChangesWarning = isProcessed && hasStagedChanges;

  // The dataset's variable entries — drive the default column visibility below
  // and the first-load skeleton columns. Usually already cached (the detail
  // page loads the version).
  const { data: versionEnvelope } = useAnalyticalDatasetVersionQuery({
    projectId,
    analyticalDatasetProfileId,
    analyticalDatasetVersionId,
  });
  const variableEntries = versionEnvelope?.data.jsonValue?.variables;
  const hasAnalyticalVariables = (variableEntries?.length ?? 0) > 0;

  // AV library lookup (the same query the Definition tab's variables list
  // uses, so it's typically cached) — resolves the dataset's variable names so
  // the skeleton can show the full expected column set while the (slower)
  // results query loads, instead of just the system columns.
  const { data: avListResponse } = useAnalyticalVariableListQuery(undefined, projectId);

  // The tab holds its full-body shimmer until the column metadata is known —
  // version (variable ids) + AV library (names) — so the grid never mounts
  // with a partial header row (2 system columns, or raw avId UUIDs standing
  // in for names that haven't resolved).
  const isColumnMetadataLoading =
    isProcessed && (!versionEnvelope || (hasAnalyticalVariables && !avListResponse));

  const skeletonColumns = useMemo<GridColDef<AnalyticalDatasetResultRow>[]>(() => {
    // Belt-and-braces for the metadata gate above — never render avId headers.
    if (!avListResponse) return SYSTEM_SKELETON_COLUMNS;
    const avItemsById = new Map(
      avListResponse.data.map((item) => [item.analyticalVariableId, item])
    );
    // field uses the avId as a placeholder — the real server field (the CSV
    // header slug) replaces it when the first response lands; the visible
    // header text matches, so the swap doesn't jump. Lookups missing from the
    // loaded list (deleted AVs) fall back to the avId, like the backend does.
    const avColumns = (variableEntries ?? []).map<GridColDef<AnalyticalDatasetResultRow>>(
      ({ avId }) => {
        const item = avItemsById.get(avId);
        return {
          field: avId,
          headerName: item?.dataElement || item?.name || avId,
          type: item ? SKELETON_TYPE_BY_FORMAT[item.format.toLowerCase()] : undefined,
        };
      }
    );
    return [...SYSTEM_SKELETON_COLUMNS, ...avColumns];
  }, [avListResponse, variableEntries]);

  // Default demographic visibility: hidden when AVs are present, shown otherwise.
  // Controlled so the user's column-toggle choices override the default once made.
  const defaultVisibility = useMemo<GridColumnVisibilityModel>(
    () =>
      hasAnalyticalVariables
        ? Object.fromEntries(DEMOGRAPHIC_FIELDS.map((field) => [field, false]))
        : {},
    [hasAnalyticalVariables]
  );
  const [visibilityOverride, setVisibilityOverride] = useState<GridColumnVisibilityModel | null>(
    null
  );
  const columnVisibilityModel = visibilityOverride ?? defaultVisibility;

  // Called as a hook by the shared DataGrid with its serialized grid params
  // (mirrors useCohortSubjectList's useFetchSubjectListData adapter).
  const useFetchResultsGridData = useCallback(
    (searchParams?: string) =>
      // eslint-disable-next-line react-hooks/rules-of-hooks -- invoked as a hook inside DataGrid's render
      useAnalyticalDatasetResultsQuery({
        projectId,
        analyticalDatasetProfileId,
        searchParams,
        enabled: isProcessed,
      }),
    [projectId, analyticalDatasetProfileId, isProcessed]
  );

  // Backend-supported filter operators per MUI column type; boolean keeps MUI's
  // default (`is` only, which the backend translates).
  const stringOperators = useMemo(
    () => getGridStringOperators().filter((op) => SUPPORTED_STRING_OPERATORS.has(op.value)),
    []
  );
  const numericOperators = useMemo(
    () => getGridNumericOperators().filter((op) => SUPPORTED_NUMERIC_OPERATORS.has(op.value)),
    []
  );
  // MUI's built-ins trimmed to the backend set, plus the custom two-date
  // `between` operator (inclusive; serialized as `field:between(start,end)`).
  const dateOperators = useMemo(
    () => [
      ...getGridDateOperators().filter((op) => SUPPORTED_DATE_OPERATORS.has(op.value)),
      buildDateBetweenFilterOperator({
        label: formatMessage({ id: 'analyticalDatasetDetail.results.filterBetweenLabel' }),
        fromLabel: formatMessage({ id: 'analyticalDatasetDetail.results.filterBetweenFrom' }),
        toLabel: formatMessage({ id: 'analyticalDatasetDetail.results.filterBetweenTo' }),
        invalidRangeHelperText: formatMessage({
          id: 'analyticalDatasetDetail.results.filterBetweenInvalidRange',
        }),
      }),
    ],
    [formatMessage]
  );

  // Server-driven columns -> GridColDef. Sorting (multi via shift-click) and
  // multi-column filtering are server-side (the shared DataGrid serializes its
  // models into the query string; the results endpoint translates them against
  // the dataset's own column allowlist). Date values arrive as ISO YYYY-MM-DD
  // strings and are formatted for display only.
  const mapServerColumns = useCallback(
    (
      serverColumns: GridColDef<AnalyticalDatasetResultRow>[]
    ): GridColDef<AnalyticalDatasetResultRow>[] =>
      (serverColumns.length === 0 ? skeletonColumns : serverColumns).map((column) => {
        const isDate = column.type === 'date' || column.type === 'dateTime';
        const isDateTime = column.type === 'dateTime';
        const isBoolean = column.type === 'boolean';
        const filterOperators: GridFilterOperator[] | undefined =
          column.type === 'number'
            ? numericOperators
            : column.type === 'date'
            ? dateOperators
            : isBoolean || isDateTime
            ? undefined
            : stringOperators;
        return {
          field: column.field,
          // Header is the human display name; the raw key (= CSV export
          // header) lives in the hover tooltip below.
          headerName: column.headerName || column.field,
          // Date columns keep their string values (no Date conversion) — MUI's
          // 'date' type expects Date objects, so they render as default/string.
          type: isDate ? undefined : column.type,
          // Server owns the width (ColumnDefinition.minWidth); skeleton columns
          // fall back to the local default.
          minWidth: column.minWidth ?? SKELETON_COLUMN_MIN_WIDTH,
          flex: 1,
          sortable: true,
          filterable: !isDateTime,
          ...(filterOperators ? { filterOperators } : {}),
          // Header hover: the full display name (long AV names truncate in the
          // header cell) plus the raw column key / CSV export header in brackets.
          description: `${column.headerName || column.field} (${column.field})`,
          // Patient ID is the row identifier — always visible; everything else
          // can be toggled from the columns panel.
          hideable: column.field !== 'patient_id',
          renderCell: (params) => {
            if (params.value === null || params.value === undefined || params.value === '') {
              return EMPTY_CELL;
            }
            if (isDate) {
              return formatIsoDate(String(params.value));
            }
            // Boolean AV values arrive as the strings "true"/"false" — title-case
            // them for display ("True"/"False").
            if (isBoolean) {
              return formatBooleanCell(String(params.value));
            }
            return String(params.value);
          },
        };
      }),
    [skeletonColumns, stringOperators, numericOperators, dateOperators]
  );

  // Gen-3-style empty state (outlined card) for every non-processed status;
  // null means the grid renders.
  const emptyState: ResultsTabEmptyState | null = isProcessed
    ? null
    : latestStatus === 'PROCESSING'
    ? {
        id: 'ad-results-processing',
        title: formatMessage({ id: 'analyticalDatasetDetail.results.processingTitle' }),
        message: formatMessage({ id: 'analyticalDatasetDetail.results.processingBody' }),
      }
    : latestStatus === 'ERROR'
    ? {
        id: 'ad-results-error',
        title: formatMessage({ id: 'analyticalDatasetDetail.results.errorTitle' }),
        message: formatMessage({ id: 'analyticalDatasetDetail.results.errorBody' }),
      }
    : {
        id: 'ad-results-not-processed',
        title: formatMessage({ id: 'analyticalDatasetDetail.results.notProcessedTitle' }),
        message: formatMessage({ id: 'analyticalDatasetDetail.results.notProcessedBody' }),
      };

  return {
    // While the profile (status) or column metadata is loading the tab shows
    // its full-body shimmer — otherwise the "not processed" card flashes
    // before the real status arrives, or the grid mounts with partial headers.
    isTabLoading: isProfileLoading || isColumnMetadataLoading,
    emptyState,
    showStagedChangesWarning,
    mapServerColumns,
    useFetchResultsGridData,
    columnVisibilityModel,
    onColumnVisibilityModelChange: setVisibilityOverride,
  };
};
